import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import type { Student, StudentFormValues } from '../types/student';
import { withTimeout } from '../utils/async';
import { STUDENTS_COLLECTION, STUDENT_PHOTOS_PATH, db, describeFirebaseError, storage } from '../utils/firebase';
import { useAuth } from './AuthContext';

/** 'synced' = the server acknowledged, 'pending' = queued locally by Firestore. */
export type WriteResult = 'synced' | 'pending';
const WRITE_TIMEOUT = 7000;

/** Firestore queues offline writes, so a slow ack must never block the UI. */
async function settle(write: Promise<unknown>): Promise<WriteResult> {
  write.catch(() => undefined);
  try {
    await withTimeout(write, WRITE_TIMEOUT, 'Firestore write');
    return 'synced';
  } catch (error) {
    if (error instanceof Error && error.name === 'TimeoutError') return 'pending';
    throw error;
  }
}
interface StudentsContextValue {
  students: Student[];
  loading: boolean;
  error: string | null;
  formOpen: boolean;
  editing: Student | null;
  detail: Student | null;
  openCreateForm: () => void;
  openEditForm: (student: Student) => void;
  closeForm: () => void;
  openDetail: (student: Student) => void;
  closeDetail: () => void;
  createStudent: (values: StudentFormValues, photo: File | null) => Promise<WriteResult>;
  updateStudent: (student: Student, values: StudentFormValues, photo: File | null, removePhoto: boolean) => Promise<WriteResult>;
  removeStudent: (student: Student) => Promise<WriteResult>;
}
const StudentsContext = createContext<StudentsContextValue | undefined>(undefined);
function toStudent(id: string, data: Record<string, unknown>): Student {
  const text = (key: string) => String(data[key] ?? '');
  const createdAtMs = Number(data.createdAtMs ?? 0);
  return {
    id,
    ownerId: text('ownerId'),
    fullNames: text('fullNames'),
    gender: text('gender') as Student['gender'] || '',
    className: text('className'),
    fatherName: text('fatherName'),
    fatherPhone: text('fatherPhone'),
    motherName: text('motherName'),
    motherPhone: text('motherPhone'),
    note: text('note'),
    photoURL: text('photoURL'),
    photoPath: text('photoPath'),
    createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
    updatedAtMs: Number(data.updatedAtMs ?? createdAtMs) || 0
  };
}
export function StudentsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    user
  } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [detail, setDetail] = useState<Student | null>(null);

  // Live Firestore listener — the table always mirrors the database.
  useEffect(() => {
    if (!user) {
      setStudents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const studentsQuery = query(collection(db, STUDENTS_COLLECTION), where('ownerId', '==', user.uid));
    const unsubscribe = onSnapshot(studentsQuery, snapshot => {
      const next = snapshot.docs.map(document => toStudent(document.id, document.data() as Record<string, unknown>));
      next.sort((a, b) => b.createdAtMs - a.createdAtMs);
      setStudents(next);
      setLoading(false);
      setError(null);
    }, snapshotError => {
      setError(describeFirebaseError(snapshotError));
      setLoading(false);
    });
    return unsubscribe;
  }, [user]);
  const uploadPhoto = useCallback(async (file: File) => {
    if (!user) throw new Error('You must be signed in to upload a photo.');
    const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
    const path = `${STUDENT_PHOTOS_PATH}/${user.uid}/${Date.now()}-${safeName}`;
    const storageRef = ref(storage, path);
    await withTimeout(uploadBytes(storageRef, file, {
      contentType: file.type
    }), 45000, 'Photo upload');
    const url = await withTimeout(getDownloadURL(storageRef), 20000, 'Photo link');
    return {
      path,
      url
    };
  }, [user]);
  const createStudent = useCallback(async (values: StudentFormValues, photo: File | null) => {
    if (!user) throw new Error('You must be signed in to add a student.');
    let photoURL = '';
    let photoPath = '';
    if (photo) {
      const uploaded = await uploadPhoto(photo);
      photoURL = uploaded.url;
      photoPath = uploaded.path;
    }
    const now = Date.now();
    return settle(addDoc(collection(db, STUDENTS_COLLECTION), {
      ...values,
      photoURL,
      photoPath,
      ownerId: user.uid,
      createdAtMs: now,
      updatedAtMs: now,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }));
  }, [uploadPhoto, user]);
  const updateStudent = useCallback(async (student: Student, values: StudentFormValues, photo: File | null, removePhoto: boolean) => {
    let photoURL = student.photoURL;
    let photoPath = student.photoPath;
    if (photo) {
      const uploaded = await uploadPhoto(photo);
      photoURL = uploaded.url;
      photoPath = uploaded.path;
      if (student.photoPath) {
        void deleteObject(ref(storage, student.photoPath)).catch(() => undefined);
      }
    } else if (removePhoto && student.photoPath) {
      void deleteObject(ref(storage, student.photoPath)).catch(() => undefined);
      photoURL = '';
      photoPath = '';
    } else if (removePhoto) {
      photoURL = '';
      photoPath = '';
    }
    return settle(updateDoc(doc(db, STUDENTS_COLLECTION, student.id), {
      ...values,
      photoURL,
      photoPath,
      updatedAtMs: Date.now(),
      updatedAt: serverTimestamp()
    }));
  }, [uploadPhoto]);
  const removeStudent = useCallback(async (student: Student) => {
    const result = await settle(deleteDoc(doc(db, STUDENTS_COLLECTION, student.id)));
    if (student.photoPath) {
      void deleteObject(ref(storage, student.photoPath)).catch(() => undefined);
    }
    return result;
  }, []);
  const openCreateForm = useCallback(() => {
    setEditing(null);
    setFormOpen(true);
  }, []);
  const openEditForm = useCallback((student: Student) => {
    setEditing(student);
    setFormOpen(true);
  }, []);
  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditing(null);
  }, []);
  const openDetail = useCallback((student: Student) => setDetail(student), []);
  const closeDetail = useCallback(() => setDetail(null), []);
  const value = useMemo<StudentsContextValue>(() => ({
    students,
    loading,
    error,
    formOpen,
    editing,
    detail,
    openCreateForm,
    openEditForm,
    closeForm,
    openDetail,
    closeDetail,
    createStudent,
    updateStudent,
    removeStudent
  }), [students, loading, error, formOpen, editing, detail, openCreateForm, openEditForm, closeForm, openDetail, closeDetail, createStudent, updateStudent, removeStudent]);
  return <StudentsContext.Provider value={value} data-id="element-478" data-mp="3qjg2rlqdogk9">{children}</StudentsContext.Provider>;
}
export function useStudents(): StudentsContextValue {
  const context = useContext(StudentsContext);
  if (!context) throw new Error('useStudents must be used inside StudentsProvider');
  return context;
}